import { useMemo } from 'react';
import { Box, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { ColDef, RowStyle } from 'ag-grid-community';
import type { RowClassParams } from 'ag-grid-community/dist/types/core/entities/gridOptions';
import { BudgetScenarioImpactResult } from '@callmart/types';
import { useTranslation } from 'react-i18next';
import TableGridItem from '../UI/TableGridItem';
import {
    formatPercent,
    generateMonthColumns,
    integerFormatter,
    SCENARIO_GRID_CLASS,
    SCENARIO_HEADER_HEIGHT,
    SCENARIO_ROW_HEIGHT,
    SCENARIO_MONTH_COL_WIDTH,
    scenarioEntityColumn,
    scenarioFillerColumn,
} from './helpers';

/** Which of the two communication metrics the table renders. */
export type BudgetImpactView = 'relative' | 'absolute';

interface ScenarioBudgetImpactGridProps {
    impact: BudgetScenarioImpactResult | undefined;
    isLoading: boolean;
    view: BudgetImpactView;
    // The form has unsaved edits AND they could not be previewed, so these numbers were computed from the
    // LAST SAVED inputs — the table is dimmed to say it is not describing what is on screen. The words for it
    // are ScenarioImpactBadge's, in the section heading (CALL-784).
    isStale?: boolean;
}

// The Relativ/Absolut segmented control, rendered by the section header rather than by the grid: the header
// already carries the input⇄impact toggle, and a second dashed-underline pair there would be ambiguous
// about which axis is which. Exported from this file so the two labels and the view type stay next to the
// only grid that reads them.
export const ScenarioBudgetImpactViewToggle = ({
    value,
    onChange,
}: {
    value: BudgetImpactView;
    onChange: (next: BudgetImpactView) => void;
}) => {
    const { t } = useTranslation('budget');
    return (
        <ToggleButtonGroup
            size="small"
            exclusive
            value={value}
            onChange={(_e, next: BudgetImpactView | null) => next && onChange(next)}
            // Held to the height of the small outlined buttons elsewhere in this header. At the theme's
            // default the group measured 39px against a heading row that is otherwise 24-30px, so the
            // budget-adjustment page moved 4px on every input⇄impact toggle (CALL-784).
            sx={{ height: 30, '& .MuiToggleButton-root': { py: 0 } }}
        >
            <ToggleButton value="relative">
                <span>{t('scenarioDetail.relativeView')}</span>
            </ToggleButton>
            <ToggleButton value="absolute">
                <span>{t('scenarioDetail.absoluteView')}</span>
            </ToggleButton>
        </ToggleButtonGroup>
    );
};

// Budget-adjustment impact display (read-only). The budget-side euro delta itself is shown in the
// consolidated view and the input grid; here the delta is translated into the client's two
// communication views over the selected worktype scope: Relativ (the uniform percentage of the
// scope's cost) and Absolut (the contacts/calls that can no longer be handled). A reduction shows as
// negative in both. Which metric is shown is the section's decision, not this grid's.
const ScenarioBudgetImpactGrid = ({ impact, isLoading, view, isStale }: ScenarioBudgetImpactGridProps) => {
    const { t } = useTranslation('budget');

    const months = impact?.months ?? [];

    const cellValue = (
        cell: { relativePct?: number; absoluteContacts?: number } | undefined,
        current: BudgetImpactView
    ): number => (current === 'relative' ? (cell?.relativePct ?? 0) : (cell?.absoluteContacts ?? 0));

    const rowData = useMemo(() => {
        if (!impact) return [];
        return impact.rows.map((row) => {
            const data: Record<string, unknown> = { entityId: row.entityId, entityLabel: row.entityLabel };
            for (const month of impact.months) data[month] = cellValue(row.monthly[month], view);
            return data;
        });
    }, [impact, view]);

    // Relativ is uniform across the scope, so the total is that same percentage (taken from any row),
    // not a sum; Absolut sums the per-worktype calls to the scope total.
    const pinnedBottomRowData = useMemo(() => {
        if (!impact || impact.rows.length === 0) return [];
        const totals: Record<string, unknown> = { entityLabel: t('summary.total') };
        for (const month of impact.months) {
            if (view === 'relative') {
                totals[month] = impact.rows[0].monthly[month]?.relativePct ?? 0;
            } else {
                totals[month] = impact.rows.reduce((sum, row) => sum + (row.monthly[month]?.absoluteContacts ?? 0), 0);
            }
        }
        return [totals];
    }, [impact, view, t]);

    const formatValue = (value: unknown): string => {
        if (value == null || value === '') return '';
        return view === 'relative' ? formatPercent(value) : integerFormatter.format(Number(value));
    };

    const columnDefs: ColDef[] = useMemo(
        () => [
            { ...scenarioEntityColumn(t('field.worktype')), field: 'entityLabel' },
            ...generateMonthColumns(months, {
                // A `width`, not the helper's minWidth: month columns are flex:0, where ag-grid sizes by
                // width and defaults it to 200px — see SCENARIO_MONTH_COL_WIDTH.
                width: SCENARIO_MONTH_COL_WIDTH,
                valueFormatter: (params) => formatValue(params.value),
            }),
            // Soaks up the width a short period leaves over, so the table fills its own shell (CALL-784).
            scenarioFillerColumn(),
        ],
        // formatValue depends on view; months/t are the other inputs.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [t, months, view]
    );

    const defaultColDef: ColDef = useMemo(
        // No filters on this read-only grid → hide the header menu (kebab), like the summary grids.
        () => ({ editable: false, sortable: true, filter: false, resizable: true, suppressMenu: true }),
        []
    );

    const getRowStyle = (params: RowClassParams): RowStyle | undefined =>
        params.node.rowPinned === 'bottom' ? { fontWeight: 'bold' } : undefined;

    return (
        <Box className={SCENARIO_GRID_CLASS} sx={{ opacity: isStale ? 0.6 : 1 }}>
            <TableGridItem
                height="auto"
                domLayout="autoHeight"
                headerHeight={SCENARIO_HEADER_HEIGHT}
                rowHeight={SCENARIO_ROW_HEIGHT}
                rowData={rowData}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                pinnedBottomRowData={pinnedBottomRowData}
                getRowStyle={getRowStyle}
                getRowId={(params) => String(params.data.entityId ?? 'total')}
                loading={isLoading}
                noRowsOverlayComponent={() => <span style={{ padding: 10 }}>{t('scenarioDetail.noImpact')}</span>}
            />
        </Box>
    );
};

export default ScenarioBudgetImpactGrid;
